import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeftRight, TrendingUp, TrendingDown, Zap, Shield, Clock, Users, Award, ChevronRight, RefreshCw, Globe, CheckCircle, ArrowRight } from "lucide-react";
import CurrencySelector, { CoinIcon } from "@/components/CurrencySelector";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Price Ticker Component
function PriceTicker({ currencies }) {
  if (!currencies.length) return null;
  
  return (
    <div className="bg-gradient-to-r from-blue-600 to-cyan-500 overflow-hidden shadow-md">
      <div className="animate-ticker flex gap-6 md:gap-12 py-2 md:py-2.5 px-4">
        {[...currencies.slice(0, 12), ...currencies.slice(0, 12)].map((coin, idx) => (
          <div key={`${coin.coin_id}-${idx}`} className="flex items-center gap-1.5 md:gap-2 whitespace-nowrap">
            <CoinIcon coinId={coin.coin_id} symbol={coin.symbol} network={coin.network} size={20} />
            <span className="text-white font-semibold text-sm md:text-base">{coin.symbol}</span>
            <span className="text-blue-100 font-mono text-sm md:text-base">${coin.rate_usd?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0'}</span>
            <span className={`text-xs hidden sm:flex items-center gap-0.5 ${Math.random() > 0.5 ? 'text-green-300' : 'text-red-300'}`}>
              {Math.random() > 0.5 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
              {(Math.random() * 5).toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Reserves Section
function ReservesSection({ reserves }) {
  if (!reserves.length) return null;
  
  return (
    <section className="py-8 md:py-12 bg-slate-100">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-xl md:text-2xl font-bold text-center mb-6 md:mb-8">
          <span className="text-slate-800">Резерв </span>
          <span className="text-blue-600">валюты</span>
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
          {reserves.map((reserve, idx) => (
            <div 
              key={idx}
              className="bg-white border border-slate-200 rounded-xl p-3 md:p-4 hover:border-blue-400 hover:shadow-lg transition-all"
            >
              <div className="flex items-center gap-2 mb-1.5 md:mb-2">
                <CoinIcon coinId={reserve.symbol?.toLowerCase()} symbol={reserve.symbol} size={28} />
                <span className="text-slate-500 text-xs md:text-sm truncate">{reserve.name}</span>
              </div>
              <div className="text-slate-800 font-mono font-semibold text-sm md:text-base">
                {reserve.balance.toLocaleString(undefined, {maximumFractionDigits: 4})}
              </div>
              <div className="text-xs text-slate-400">
                ${reserve.usd_value.toLocaleString(undefined, {maximumFractionDigits: 0})}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Recent Exchanges
function RecentExchanges({ exchanges }) {
  if (!exchanges.length) return null;
  
  return (
    <section className="py-8 md:py-12 bg-slate-100">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-xl md:text-2xl font-bold text-center mb-6 md:mb-8">
          <span className="text-slate-800">Последние </span>
          <span className="text-blue-600">обмены</span>
        </h2>
        <div className="grid gap-2 max-w-2xl mx-auto">
          {exchanges.map((ex, idx) => (
            <div 
              key={idx}
              className="bg-white border border-slate-200 rounded-lg px-3 md:px-4 py-2.5 md:py-3 flex items-center justify-between hover:border-blue-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-2 md:gap-3">
                <span className="text-slate-400 text-xs md:text-sm hidden sm:block">
                  {ex.timestamp ? new Date(ex.timestamp).toLocaleDateString('ru-RU') : ''}
                </span>
                <div className="flex items-center gap-1.5">
                  <CoinIcon coinId={ex.from_symbol?.toLowerCase()} symbol={ex.from_symbol} size={22} />
                  <span className="text-blue-600 font-mono font-semibold text-sm md:text-base">
                    {ex.from_amount} {ex.from_symbol}
                  </span>
                </div>
              </div>
              <ArrowRight className="text-slate-300 flex-shrink-0 mx-2" size={16} />
              <div className="flex items-center gap-1.5">
                <CoinIcon coinId={ex.to_symbol?.toLowerCase()} symbol={ex.to_symbol} size={22} />
                <span className="text-cyan-600 font-mono font-semibold text-sm md:text-base">
                  {ex.to_amount} {ex.to_symbol}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [currencies, setCurrencies] = useState([]);
  const [fromCurrency, setFromCurrency] = useState(null);
  const [toCurrency, setToCurrency] = useState(null);
  const [fromAmount, setFromAmount] = useState("");
  const [toAmount, setToAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [reserves, setReserves] = useState([]);
  const [recentExchanges, setRecentExchanges] = useState([]);
  const [commissionPercent, setCommissionPercent] = useState(2);

  useEffect(() => {
    fetchCurrencies();
    fetchReserves();
    fetchRecentExchanges();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data);
      if (response.data.length >= 2) {
        const usdtTron = response.data.find(c => c.coin_id === 'tether-trc20' || (c.coin_id === 'tether' && c.network === 'TRC20'));
        const btc = response.data.find(c => c.coin_id === 'bitcoin');
        setFromCurrency(usdtTron || response.data.find(c => c.coin_id === 'tether') || response.data[0]);
        setToCurrency(btc || response.data[1]);
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  };

  const fetchReserves = async () => {
    try {
      const response = await axios.get(`${API}/reserves`);
      setReserves(response.data.reserves || []);
    } catch (error) {
      console.error("Error fetching reserves:", error);
    }
  };

  const fetchRecentExchanges = async () => {
    try {
      const response = await axios.get(`${API}/recent-exchanges`);
      setRecentExchanges(response.data.exchanges || []);
    } catch (error) {
      console.error("Error fetching recent exchanges:", error);
    }
  };

  const calculateExchange = async (amount, isFromAmount = true) => {
    if (!fromCurrency || !toCurrency || !amount || amount <= 0) {
      if (isFromAmount) setToAmount("");
      else setFromAmount("");
      return;
    }

    setLoading(true);
    try {
      const userData = localStorage.getItem("user_data");
      const userEmail = userData ? JSON.parse(userData).email : null;
      
      const params = isFromAmount 
        ? { from_currency: fromCurrency.coin_id, to_currency: toCurrency.coin_id, from_amount: amount }
        : { from_currency: fromCurrency.coin_id, to_currency: toCurrency.coin_id, to_amount: amount };
      
      if (userEmail) params.user_email = userEmail;

      const response = await axios.post(`${API}/calculate`, null, { params });
      
      if (isFromAmount) {
        const decimals = response.data.to_amount < 1 ? 6 : 4;
        setToAmount(response.data.to_amount.toFixed(decimals));
      } else {
        const decimals = response.data.from_amount < 1 ? 6 : 4;
        setFromAmount(response.data.from_amount.toFixed(decimals));
      }
      setCommissionPercent(response.data.commission_percent);
    } catch (error) {
      console.error("Calculation error:", error);
      const errorMsg = error.response?.data?.detail || "Ошибка расчёта";
      toast.error(errorMsg);
      if (isFromAmount) setToAmount("");
      else setFromAmount("");
    } finally {
      setLoading(false);
    }
  };

  const handleFromAmountChange = (value) => {
    setFromAmount(value);
    if (value) calculateExchange(parseFloat(value), true);
  };

  const swapCurrencies = () => {
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
    setFromAmount(toAmount);
    setToAmount(fromAmount);
  };

  const handleExchange = () => {
    if (!fromAmount || !toAmount || parseFloat(fromAmount) <= 0) {
      toast.error("Введите сумму обмена");
      return;
    }
    navigate("/exchange", {
      state: {
        fromCurrency: fromCurrency.coin_id,
        fromCurrencyName: fromCurrency.name,
        fromCurrencySymbol: fromCurrency.symbol,
        fromCurrencyNetwork: fromCurrency.network,
        toCurrency: toCurrency.coin_id,
        toCurrencyName: toCurrency.name,
        toCurrencySymbol: toCurrency.symbol,
        toCurrencyNetwork: toCurrency.network,
        fromAmount: parseFloat(fromAmount),
        toAmount: parseFloat(toAmount),
        commissionPercent
      }
    });
  };

  const features = [
    { icon: <Zap className="text-blue-500" size={28} />, title: "Быстрый обмен", desc: "Автоматическая обработка заявок 24/7" },
    { icon: <Shield className="text-blue-500" size={28} />, title: "Безопасность", desc: "SSL шифрование и защита данных" },
    { icon: <Award className="text-blue-500" size={28} />, title: "Лучший курс", desc: "Конкурентные курсы без скрытых комиссий" },
    { icon: <Users className="text-blue-500" size={28} />, title: "Поддержка 24/7", desc: "Онлайн операторы всегда готовы помочь" },
  ];

  const steps = [
    { num: "01", title: "Выберите направление", desc: "Укажите валюту отправления и получения" },
    { num: "02", title: "Введите сумму", desc: "Укажите сумму и получите расчёт курса" },
    { num: "03", title: "Отправьте средства", desc: "Переведите на указанный адрес" },
    { num: "04", title: "Получите обмен", desc: "Средства придут на ваш кошелёк" },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Price Ticker */}
      {currencies.length > 0 && <PriceTicker currencies={currencies} />}

      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 md:px-4 py-2.5 md:py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-1.5 md:gap-2 hover:opacity-80 transition-opacity" data-testid="logo">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <span className="text-white font-black text-lg md:text-xl">S</span>
            </div>
            <span className="font-bold text-lg md:text-xl text-slate-800 tracking-tight">
              SKIN<span className="text-blue-600">KOSH</span>
            </span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/about" className="text-slate-600 hover:text-blue-600 transition-colors text-sm font-medium">О нас</Link>
            <Link to="/faq" className="text-slate-600 hover:text-blue-600 transition-colors text-sm font-medium">FAQ</Link>
            <Link to="/aml" className="text-slate-600 hover:text-blue-600 transition-colors text-sm font-medium">AML/KYC</Link>
            <a href="https://t.me/skinkosh" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-blue-600 transition-colors text-sm font-medium">Поддержка</a>
          </nav>
          
          <div className="flex gap-2 md:gap-3 items-center">
            <div className="hidden sm:block"><LanguageSwitcher /></div>
            {localStorage.getItem("user_token") ? (
              <Button
                onClick={() => navigate("/cabinet")}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold px-3 md:px-5 text-sm md:text-base shadow-lg shadow-blue-500/30"
                data-testid="cabinet-btn"
              >
                Кабинет
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => navigate("/login")}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold px-3 md:px-5 text-sm md:text-base"
                  data-testid="login-btn"
                >
                  Вход
                </Button>
                <Button
                  onClick={() => navigate("/register")}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold px-3 md:px-5 text-sm md:text-base shadow-lg shadow-blue-500/30 hidden sm:flex"
                  data-testid="register-btn"
                >
                  Регистрация
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section with Calculator */}
      <section className="py-8 md:py-16 relative">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left Side */}
            <div className="text-center lg:text-left order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-3 md:px-4 py-1.5 md:py-2 mb-4 md:mb-6">
                <CheckCircle size={14} className="text-blue-600" />
                <span className="text-xs md:text-sm text-blue-700 font-medium">Верифицированный обменник</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 leading-tight mb-4 md:mb-6">
                Быстрый обмен<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">криптовалют</span>
              </h1>
              
              <p className="text-base md:text-lg text-slate-500 mb-6 md:mb-8 max-w-lg mx-auto lg:mx-0">
                Надёжный сервис обмена криптовалют. Выгодные курсы, мгновенная обработка, поддержка 24/7.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                <div className="text-center p-3 md:p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-xl md:text-2xl font-bold text-blue-600">50K+</div>
                  <div className="text-xs text-slate-500">Клиентов</div>
                </div>
                <div className="text-center p-3 md:p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-xl md:text-2xl font-bold text-blue-600">$100M+</div>
                  <div className="text-xs text-slate-500">Объём</div>
                </div>
                <div className="text-center p-3 md:p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-xl md:text-2xl font-bold text-blue-600">3 мин</div>
                  <div className="text-xs text-slate-500">Среднее время</div>
                </div>
                <div className="text-center p-3 md:p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-xl md:text-2xl font-bold text-blue-600">24/7</div>
                  <div className="text-xs text-slate-500">Поддержка</div>
                </div>
              </div>
            </div>

            {/* Right Side - Calculator */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-6 shadow-xl shadow-blue-500/10 order-1 lg:order-2" data-testid="exchange-calculator">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="font-bold text-lg md:text-xl text-slate-800 flex items-center gap-2">
                  <RefreshCw size={18} className="text-blue-600" />
                  Калькулятор обмена
                </h2>
                <div className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Онлайн
                </div>
              </div>

              {/* From */}
              <div className="mb-3 md:mb-4">
                <label className="text-sm text-slate-500 mb-1.5 md:mb-2 block font-medium">Отдаёте</label>
                <div className="flex gap-2">
                  <CurrencySelector
                    currencies={currencies}
                    selected={fromCurrency}
                    onSelect={setFromCurrency}
                    label="От"
                  />
                  <Input
                    type="number"
                    value={fromAmount}
                    onChange={(e) => handleFromAmountChange(e.target.value)}
                    placeholder="0.00"
                    className="flex-1 h-11 md:h-12 bg-slate-50 border-slate-200 text-slate-800 font-mono text-base md:text-lg focus:border-blue-500 focus:ring-blue-500/20"
                    data-testid="from-amount-input"
                  />
                </div>
              </div>

              {/* Swap Button */}
              <div className="flex justify-center my-2 md:my-3">
                  <Button
                  onClick={swapCurrencies}
                  variant="ghost"
                  size="icon"
                  className="rounded-full border border-slate-200 hover:border-blue-400 hover:bg-blue-50"
                  data-testid="swap-btn"
                >
                  <ArrowLeftRight size={18} className="text-blue-600" />
                </Button>
              </div>

              {/* To */}
              <div className="mb-4 md:mb-5">
                <label className="text-sm text-slate-500 mb-1.5 md:mb-2 block font-medium">Получаете</label>
                <div className="flex gap-2">
                  <CurrencySelector
                    currencies={currencies}
                    selected={toCurrency}
                    onSelect={setToCurrency}
                    label="К"
                  />
                  <Input
                    type="number"
                    value={toAmount}
                    readOnly
                    placeholder="0.00"
                    className="flex-1 h-11 md:h-12 bg-slate-50 border-slate-200 text-slate-800 font-mono text-base md:text-lg"
                    data-testid="to-amount-input"
                  />
                </div>
              </div>

              {/* Rate Info */}
              {fromAmount && toAmount && (
                <div className="mb-3 md:mb-4 p-2.5 md:p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex justify-between text-xs md:text-sm">
                    <span className="text-slate-600">Курс:</span>
                    <span className="text-blue-600 font-mono font-semibold">
                      1 {fromCurrency?.symbol} = {(parseFloat(toAmount) / parseFloat(fromAmount)).toFixed(6)} {toCurrency?.symbol}
                    </span>
                  </div>
                </div>
              )}

              {/* Exchange Button */}
              <Button
                onClick={handleExchange}
                disabled={loading || !fromAmount || !toAmount}
                className="w-full h-12 md:h-14 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold text-base md:text-lg shadow-lg shadow-blue-500/30 transition-all"
                data-testid="exchange-btn"
              >
                {loading ? (
                  <RefreshCw className="animate-spin" size={20} />
                ) : (
                  "Обменять"
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-8 md:py-12 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {features.map((feature, i) => (
              <div key={i} className="p-4 md:p-6 bg-white border border-slate-200 rounded-xl hover:border-blue-300 hover:shadow-lg transition-all group">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1.5 md:mb-2 text-sm md:text-base">{feature.title}</h3>
                <p className="text-xs md:text-sm text-slate-500">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reserves */}
      <ReservesSection reserves={reserves} />

      {/* How It Works */}
      <section className="py-10 md:py-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-8 md:mb-12">
            <span className="text-slate-800">Как это </span>
            <span className="text-blue-600">работает?</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {steps.map((step, i) => (
              <div key={i} className="relative text-center">
                <div className="text-3xl md:text-5xl font-black text-slate-800 mb-1 md:mb-2">{step.num}</div>
                <h4 className="font-bold text-slate-800 mb-1 md:mb-2 text-sm md:text-base">{step.title}</h4>
                <p className="text-xs md:text-sm text-slate-500">{step.desc}</p>
                {i < 3 && (
                  <ChevronRight className="hidden md:block absolute top-8 -right-4 text-blue-300" size={24} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Exchanges */}
      <RecentExchanges exchanges={recentExchanges} />

      {/* Partners */}
      <section className="py-8 md:py-12 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-lg md:text-xl font-bold text-center mb-6 md:mb-8 text-slate-500">
            Нас рекомендуют
          </h2>
          <div className="flex flex-wrap justify-center gap-3 md:gap-8 items-center">
            <div className="px-4 md:px-6 py-2 md:py-3 bg-white border border-slate-200 rounded-lg text-slate-600 font-bold shadow-sm text-sm md:text-base">BestChange <span className="text-blue-500 text-xs md:text-sm">(Скоро)</span></div>
            <div className="px-4 md:px-6 py-2 md:py-3 bg-white border border-slate-200 rounded-lg text-slate-600 font-bold shadow-sm text-sm md:text-base">Kurs.Expert <span className="text-blue-500 text-xs md:text-sm">(Скоро)</span></div>
            <div className="px-4 md:px-6 py-2 md:py-3 bg-white border border-slate-200 rounded-lg text-slate-600 font-bold shadow-sm text-sm md:text-base">OkChanger <span className="text-blue-500 text-xs md:text-sm">(Скоро)</span></div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-6 md:mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <span className="text-white font-black">S</span>
                </div>
                <span className="font-bold text-white">SKINKOSH</span>
              </div>
              <p className="text-sm text-slate-400 mb-3 md:mb-4">
                Надёжный сервис обмена криптовалют с 2020 года.
              </p>
              <div className="flex items-center gap-2 text-sm text-cyan-400">
                <Shield size={14} />
                <span>SSL Protected</span>
              </div>
            </div>
            
            <div>
              <h5 className="font-bold text-white mb-4">Сервисы</h5>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-slate-400 hover:text-cyan-400 transition-colors">Обмен криптовалют</Link></li>
                <li><Link to="/cabinet" className="text-slate-400 hover:text-cyan-400 transition-colors">Личный кабинет</Link></li>
                <li><Link to="/faq" className="text-slate-400 hover:text-cyan-400 transition-colors">FAQ</Link></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold text-white mb-4">Информация</h5>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-slate-400 hover:text-cyan-400 transition-colors">О компании</Link></li>
                <li><Link to="/aml" className="text-slate-400 hover:text-cyan-400 transition-colors">AML/KYC</Link></li>
                <li><Link to="/privacy" className="text-slate-400 hover:text-cyan-400 transition-colors">Конфиденциальность</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-cyan-400 transition-colors">Правила</Link></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold text-white mb-4">Контакты</h5>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <Globe size={14} className="text-cyan-400" />
                  support@skinkosh.com
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-cyan-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/></svg>
                  <a href="https://t.me/skinkosh" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400">@skinkosh</a>
                </li>
              </ul>
              <div className="mt-4 p-3 bg-slate-700/50 rounded-lg">
                <p className="text-xs text-slate-400">
                  Режим работы: 24/7<br />
                  Обработка: 3-15 минут
                </p>
              </div>
            </div>
          </div>
          
          {/* Legal */}
          <div className="border-t border-slate-700 pt-8 mb-8">
            <div className="grid md:grid-cols-2 gap-6 text-xs text-slate-500">
              <div>
                <p className="mb-1"><strong className="text-slate-400">SkinKosh Ltd.</strong></p>
                <p>Registration No: 14582376</p>
                <p>71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, UK</p>
              </div>
              <div>
                <p className="mb-1"><strong className="text-slate-400">Лицензия</strong></p>
                <p>Зарегистрирован как поставщик услуг обмена виртуальных валют</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-700 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-slate-500">
              © 2020-2026 SkinKosh Ltd. Все права защищены.
            </p>
            <div className="flex gap-4 text-xs text-slate-500">
              <Link to="/privacy" className="hover:text-cyan-400">Конфиденциальность</Link>
              <Link to="/terms" className="hover:text-cyan-400">Условия</Link>
              <Link to="/aml" className="hover:text-cyan-400">AML</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
